import { Linkable } from '@/components/blocks/Linkable';
import { extraLinkSX } from '@/components/content/Header/styles/account/panel';
import DynamicIcon, { IconName } from '@/components/icons/DynamicIcon';
import { useLogout } from '@/data/Content/Logout';
import { useLocalization } from '@/data/Localization';
import { useRedirectIfProtected } from '@/data/utils/useRedirectIfProtected';
import { toCamelCase } from '@/utils/toCamelCase';
import { Box, Stack, Typography } from '@mui/material';
import { SyntheticEvent } from 'react';

type ExtraLink = {
	icon: string;
	text: string;
	url?: string | { t: () => string };
	external?: boolean;
	onClick?: (event: React.SyntheticEvent) => void | Promise<void>;
	requiresAuth: boolean;
	className?: string;
};

type Props = {
	isLoggedIn: boolean;
	closeModal: () => void;
};

export const ExtraLinksSection: React.FC<Props> = ({ isLoggedIn, closeModal }) => {
	const HeaderAccountPanelLabels = useLocalization('HeaderAccountPanel');
	const { handleLogout } = useLogout();
	const { redirectIfProtected } = useRedirectIfProtected();
	const handleLogoutAction = (e: SyntheticEvent) => {
		redirectIfProtected();
		handleLogout(e);
		closeModal();
	};
	const invoiceURL = 'https://facturacion.homedepot.com.mx';

	const extraLinks: ExtraLink[] = [
		{
			icon: HeaderAccountPanelLabels.ExtraLinks.Facturacion.Icon.t(),
			text: HeaderAccountPanelLabels.ExtraLinks.Facturacion.Text.t(),
			url: invoiceURL,
			external: true,
			requiresAuth: false,
			className: 'account-panel__billing',
		},
		{
			icon: HeaderAccountPanelLabels.ExtraLinks.Ayuda.Icon.t(),
			text: HeaderAccountPanelLabels.ExtraLinks.Ayuda.Text.t(),
			url: '/ayuda',
			requiresAuth: false,
			className: 'account-panel__help',
		},
		{
			icon: HeaderAccountPanelLabels.ExtraLinks.CloseSession.Icon.t(),
			text: HeaderAccountPanelLabels.ExtraLinks.CloseSession.Text.t(),
			url: '/',
			onClick: handleLogoutAction,
			requiresAuth: true,
			className: 'account-panel__logout',
		},
	];

	return (
		<Stack sx={extraLinkSX.container}>
			{extraLinks
				.filter((item) => !item.requiresAuth || isLoggedIn)
				.map((item, index) => {
					const href = typeof item.url === 'string' ? item.url : item.url?.t();

					return (
						<Stack key={item.text + index} sx={extraLinkSX.itemContainer}>
							<Linkable
								href={href}
								onClick={item.onClick}
								sx={extraLinkSX.itemLink}
								target={item.external ? '_blank' : undefined}
								rel={item.external ? 'noopener noreferrer' : undefined}
								className={item.className}
							>
								<Box display="flex" alignItems="center">
									<DynamicIcon
										name={toCamelCase(item.icon) as IconName}
										aria-label={item.text}
										sx={{ color: 'secondary.main' }}
									/>
									<Typography variant="bodyBaseline" sx={extraLinkSX.itemText}>
										{item.text}
									</Typography>
								</Box>
							</Linkable>
						</Stack>
					);
				})}
		</Stack>
	);
};
